import React, { useEffect, useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useNavigate, useParams, Navigate, useLocation } from "react-router-dom";
import TopNav from "./components/TopNav";
import UpdateChecker from "./components/UpdateChecker";
import GoNowBanner from "./components/GoNowBanner";
import "./lib/leafletPopupTail";
import { sendVisitBeacon } from "./lib/visitBeacon";
import Dashboard from "./pages/Dashboard";
import IncidentDetail from "./pages/IncidentDetail";
import IncidentSummary from "./pages/IncidentSummary";
import SharePreviewPage from "./pages/SharePreviewPage";
import IncidentList from "./pages/IncidentList";
import Cameras from "./pages/Cameras";
import About from "./pages/About";
import Guide from "./pages/Guide";
import ComparePage from "./pages/ComparePage";
import DonatePage from "./pages/DonatePage";
import EscalationsPage from "./pages/EscalationsPage";
import HistoryPage from "./pages/HistoryPage";
import SeasonRecapPage from "./pages/SeasonRecapPage";
import EmailArchivePage from "./pages/EmailArchivePage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import ProfilePage from "./pages/ProfilePage";
import AdminPage from "./pages/AdminPage";
import ApplyReporterPage from "./pages/ApplyReporterPage";
import ReportersPage from "./pages/ReportersPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import WhatsNewPage from "./pages/WhatsNewPage";
import SystemStatusFooter from "./components/SystemStatusFooter";
import SiteFooter from "./components/SiteFooter";
import PWAInstallBanner from "./components/PWAInstallBanner";
import { refreshNow, fetchStats } from "./lib/api";
import { relTime } from "./lib/incidents";
import { CountyProvider, useCounty, CountyOverride } from "./lib/CountyContext";
import { SavedLocationsProvider } from "./lib/SavedLocationsContext";
import { AuthProvider } from "./lib/AuthContext";
import GoogleAuthCallback from "./components/GoogleAuthCallback";
import { preloadAppConfig } from "./lib/config";
import { Toaster } from "./components/ui/sonner";

// Kick off the /api/config fetch as soon as the module loads so it's warm
// by the time any component (push subscribe, feature flags, etc.) needs it.
preloadAppConfig();

const REGION_SLUGS = ["washington", "bcborder"];
const LASTVIEW_KEY = "okwatch:lastview";

function RegionDashboard() {
  const { slug } = useParams();
  useEffect(() => {
    if (REGION_SLUGS.includes(slug)) {
      try { localStorage.setItem(LASTVIEW_KEY, slug); } catch { /* noop */ }
    }
  }, [slug]);
  if (!REGION_SLUGS.includes(slug)) return <Navigate to="/" replace />;
  return (
    <CountyOverride slug={slug}>
      <Dashboard />
    </CountyOverride>
  );
}

/** Restore the member's last-used view (state/BC region) once per browser
 *  session — new users still land on Okanogan County. */
function ViewRestorer() {
  const navigate = useNavigate();
  const loc = useLocation();
  useEffect(() => {
    try {
      if (sessionStorage.getItem("okwatch:view-restored")) return;
      sessionStorage.setItem("okwatch:view-restored", "1");
      const lv = localStorage.getItem(LASTVIEW_KEY);
      if (REGION_SLUGS.includes(lv) && loc.pathname === "/") {
        navigate(`/region/${lv}`, { replace: true });
      }
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

function Shell() {
  const { county } = useCounty();
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const navigate = useNavigate();

  // Bridge service-worker notification clicks → React Router.
  // When the SW posts "firewatch:navigate" (from notificationclick), we
  // navigate internally. This is the reliable path on Android where
  // client.navigate() and openWindow() are both flaky about deep-linking.
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.serviceWorker) return;
    const handler = (event) => {
      const data = event.data;
      if (data && data.type === "firewatch:navigate" && typeof data.url === "string") {
        try {
          navigate(data.url);
          window.focus?.();
        } catch {
          window.location.href = data.url;
        }
      }
    };
    navigator.serviceWorker.addEventListener("message", handler);
    // Pull any notification-click target the SW couldn't deliver while the
    // app was suspended (otherwise a tap just focuses the last-open page).
    const pull = () => {
      try {
        navigator.serviceWorker.controller?.postMessage({ type: "firewatch:pull-nav" });
      } catch { /* no active SW yet */ }
    };
    pull();
    const onVisible = () => { if (!document.hidden) pull(); };
    window.addEventListener("focus", pull);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      navigator.serviceWorker.removeEventListener("message", handler);
      window.removeEventListener("focus", pull);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [navigate]);

  useEffect(() => {
    let stop = false;
    const tick = () => {
      fetchStats(county)
        .then((s) => { if (!stop && s?.last_refresh) setLastRefresh(relTime(s.last_refresh)); })
        .catch(() => {});
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => { stop = true; clearInterval(id); };
  }, [county]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshNow();
      await new Promise((r) => setTimeout(r, 1500));
      const s = await fetchStats(county);
      if (s?.last_refresh) setLastRefresh(relTime(s.last_refresh));
    } finally {
      setRefreshing(false);
    }
  };

  const loc = useLocation();
  const isMapPage = loc.pathname === "/" || loc.pathname.startsWith("/region/");

  useEffect(() => { sendVisitBeacon(); }, []);

  // Google OAuth return: #session_id=... in the URL fragment. Checked
  // synchronously during render (per auth playbook) so the callback runs
  // before any protected-route/session checks.
  if (loc.hash?.includes("session_id=")) {
    return <GoogleAuthCallback />;
  }

  return (
    <div className="App">
      <ViewRestorer />
      <UpdateChecker />
      <TopNav onRefresh={handleRefresh} refreshing={refreshing} lastRefresh={lastRefresh} />
      <GoNowBanner />
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/region/:slug" element={<RegionDashboard />} />
        <Route path="/incident/:id" element={<IncidentDetail />} />
        <Route path="/incident/:id/summary" element={<IncidentSummary />} />
        <Route path="/incident/:id/share" element={<SharePreviewPage />} />
        <Route path="/incidents" element={<IncidentList />} />
        <Route path="/cameras" element={<Cameras />} />
        <Route path="/history" element={<HistoryPage />} />
        <Route path="/season-recap" element={<SeasonRecapPage />} />
        <Route path="/updates" element={<EmailArchivePage />} />
        <Route path="/about" element={<About />} />
        <Route path="/whats-new" element={<WhatsNewPage />} />
        <Route path="/guide" element={<Guide />} />
        <Route path="/compare" element={<ComparePage />} />
        <Route path="/donate" element={<DonatePage />} />
        <Route path="/escalations" element={<EscalationsPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/apply-reporter" element={<ApplyReporterPage />} />
        <Route path="/reporters" element={<ReportersPage />} />
      </Routes>
      {!isMapPage && <SiteFooter />}
      {loc.pathname === "/admin" && <SystemStatusFooter />}
      <PWAInstallBanner />
      <Toaster position="bottom-right" theme="dark" richColors />
    </div>
  );
}

export default function App() {
  return (
    <CountyProvider>
      <BrowserRouter>
        <AuthProvider>
          <SavedLocationsProvider>
            <Shell />
          </SavedLocationsProvider>
        </AuthProvider>
      </BrowserRouter>
    </CountyProvider>
  );
}
